import * as React from 'react';
import styled, { css } from 'styled-components';

import { Checkbox as DSCheckbox } from '../../primitives/Checkbox';
import { getSemanticAttributes } from '../../internals/semanticProps';
import { mediaQuery } from '../../tokens/breakpoints';
import { rem } from '../../tokens/spacing';

// ---------------------------------------------------------------------------
// Context — shared state across compound components
// ---------------------------------------------------------------------------

interface SortState {
  column: string | null;
  direction: 'asc' | 'desc' | '';
}

interface DataTableContextValue {
  bordered: boolean;
  sortState: SortState;
  onSortChange: ((column: string, direction: 'asc' | 'desc' | '') => void) | undefined;
  requestSort: (accessor: string, sortable?: boolean) => void;
}

const DataTableContext = React.createContext<DataTableContextValue | null>(null);

function useDataTableContext(): DataTableContextValue {
  const ctx = React.useContext(DataTableContext);
  if (!ctx) throw new Error('DataTable compound components must be used within <DataTable.Root>');
  return ctx;
}

// ---------------------------------------------------------------------------
// Styled primitives — production-matched visual tokens
// ---------------------------------------------------------------------------

const RootWrapper = styled.div.withConfig({
  shouldForwardProp: (prop) => !['$border'].includes(String(prop)),
})<{ $border?: string }>`
  background: var(--ui-surface);
  border-radius: var(--ui-radius-md, ${rem(6)});
  border: ${({ $border }) =>
    $border ?? `${rem(1)} solid var(--ui-table-border, var(--ui-border-muted))`};
  overflow: visible;

  &.report-table--wrapper .columns-space {
    margin-bottom: ${rem(3)};
  }

  &.report-table--wrapper .columns-space-header {
    margin-bottom: ${rem(33)};
  }
`;

const ScrollContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !['$height', '$padding', '$overflowY'].includes(String(prop)),
})<{ $height?: string; $padding?: string; $overflowY?: boolean }>`
  background-color: var(--ui-surface);
  overflow-x: auto;
  ${({ $overflowY }) => $overflowY !== false && `overflow-y: auto;`}
  border-radius: var(--ui-radius-md, 0.6rem);
  padding: ${({ $padding }) => $padding ?? `0 ${rem(12)}`};
  ${({ $height }) => $height && `max-height: ${$height};`}

  &:has(.dt-v2-virtualized) {
    overflow-anchor: none;
  }

  ${mediaQuery.sm} {
    ${({ $padding }) => !$padding && `padding: 0 ${rem(20)};`}
  }

  ${mediaQuery.md} {
    ${({ $padding }) => !$padding && `padding: 0 ${rem(32)};`}
  }

  ::-webkit-scrollbar {
    height: ${rem(5)};
    width: ${rem(5)};
  }
  ::-webkit-scrollbar-track {
    background: var(--ui-scrollbar-track, #f1f1f1);
    border-radius: var(--ui-radius-md, ${rem(7)});
  }
  ::-webkit-scrollbar-thumb {
    background: var(--ui-scrollbar-thumb, #a6a6a6);
    border-radius: var(--ui-radius-md, ${rem(7)});
  }
  ::-webkit-scrollbar-thumb:hover {
    background: var(--ui-scrollbar-thumb-hover, #555555);
  }
`;

const StyledTable = styled.table.withConfig({
  shouldForwardProp: (prop) => !['$bordered'].includes(String(prop)),
})<{ $bordered?: boolean }>`
  border: none;
  border-collapse: separate;
  width: 100%;
  border-spacing: 0;
  border-radius: var(--ui-radius-md, ${rem(6)});
  font-family: var(--ui-font-sans);

  td,
  th {
    border: none;
  }
  th {
    background: var(--ui-table-header-bg, var(--ui-surface));
    cursor: pointer;
  }

  /* ---- Virtual scroll stability ---- */
  &.dt-v2-virtualized {
    overflow-anchor: none;
  }
  &.dt-v2-virtualized tbody tr td {
    transition: none;
  }

  /* ---- Skeleton shimmer for virtual scroll placeholder rows ---- */
  @keyframes dt-v2-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
  .dt-v2-skeleton-row td {
    padding: ${rem(8)} ${rem(12)};
    border-bottom: ${rem(1)} solid var(--ui-border-muted, #e2e8f0);
  }
  .dt-v2-skeleton-cell {
    background: linear-gradient(90deg, var(--ui-skeleton-from, #f1f5f9) 25%, var(--ui-skeleton-via, #e2e8f0) 50%, var(--ui-skeleton-from, #f1f5f9) 75%);
    background-size: 200% 100%;
    animation: dt-v2-shimmer 1.5s infinite linear;
  }

  /* ---- thead: sticky header + cell styles ---- */
  thead {
    top: 0;
    position: sticky;
    z-index: 2;
    box-shadow: 0 ${rem(2)} ${rem(2)} ${rem(-2)} rgb(0 0 0 / 10%);

    tr th {
      padding: ${rem(16)} ${rem(10)} ${rem(12)} ${rem(3)};
      font-weight: 900;
      font-size: ${rem(14)};
      line-height: ${rem(18)};
      text-align: left;
      color: var(--ui-table-header-color, #717171);
      white-space: normal;
      border-bottom: ${rem(1)} solid var(--ui-table-border, var(--ui-border-muted));

      ${mediaQuery.md} {
        padding: ${rem(24)} ${rem(14)} ${rem(18)} ${rem(3)};
      }

      ${mediaQuery.lg} {
        padding: ${rem(35)} ${rem(14)} ${rem(27)} ${rem(3)};
      }

      &:last-child {
        text-align: center;
      }

      & > div {
        font-weight: 900;
      }
    }

    th.custom-header {
      padding: 0;
      cursor: auto;
      border-bottom: 0;

      .custom-header--main {
        background-color: var(--ui-text-muted, #888);
        padding: ${rem(5)} ${rem(20)};
        border-radius: ${rem(15)};
        justify-content: center !important;
        margin: 0 ${rem(4)};

        span {
          color: var(--ui-surface, #fff);
        }
      }
    }
  }

  /* ---- Embedded tables (view engine / modals): keep base header padding ---- */
  &.dt-v2-embedded {
    thead tr th {
      padding: ${rem(16)} ${rem(10)} ${rem(12)} ${rem(3)};

      ${mediaQuery.md} {
        padding: ${rem(16)} ${rem(10)} ${rem(12)} ${rem(3)};
      }

      ${mediaQuery.lg} {
        padding: ${rem(16)} ${rem(10)} ${rem(12)} ${rem(3)};
      }
    }
  }

  /* ---- Sub-row header ---- */
  .subrow-header {
    box-shadow: 0 ${rem(2)} ${rem(2)} ${rem(-2)} rgb(0 0 0 / 10%);

    th {
      padding: ${rem(20)} ${rem(14)} ${rem(20)} ${rem(3)};
      font-weight: 500;
      font-size: ${rem(14)};
      line-height: ${rem(18)};
      color: var(--ui-table-header-color, #717171);
      white-space: nowrap;
    }

    th.custom-header {
      padding: 0;
      cursor: auto;
      border-bottom: 0;

      .custom-header--main {
        background-color: var(--ui-text-muted, #888);
        padding: ${rem(5)} ${rem(20)};
        border-radius: ${rem(15)};
        justify-content: center !important;
        margin: 0 ${rem(4)};

        span {
          color: var(--ui-surface, #fff);
        }
      }
    }
  }

  /* ---- td: base cell styles ---- */
  td {
    padding: ${rem(10)} ${rem(8)} ${rem(10)} ${rem(4)};
    font-style: normal;
    font-weight: 400;
    font-size: ${rem(14)};
    line-height: ${rem(18)};
    color: var(--ui-table-cell-color, #161616);
    white-space: nowrap;

    ${mediaQuery.md} {
      padding: ${rem(14)} 0;
      padding-right: ${rem(14)};
      padding-left: ${rem(5)};
    }

    .hasError {
      position: relative;
      &::before {
        content: '';
        top: 0;
        left: 0;
        position: absolute;
        height: 100%;
        width: 100%;
        background-color: var(--ui-error-light, rgba(220, 53, 69, 0.08));
      }
    }
  }

  /* ---- tbody: row hover + borders ---- */
  tbody tr {
    td {
      transition: all 0.2s ease-in-out;
      border-bottom: ${rem(1)} solid var(--ui-table-border, var(--ui-border-muted));
    }
    &:hover {
      background-color: var(--ui-table-hover, var(--ui-background-muted));
    }
    td:last-child {
      text-align: center;
    }
  }

  /* ---- Bordered variant ---- */
  ${({ $bordered }) =>
    $bordered &&
    css`
      th,
      td {
        border-right: 0.5px solid rgba(166, 166, 166, 0.25);
        &:last-child {
          border-right: none;
        }
      }
    `}

  /* ---- Report table variant ---- */
  &.report-table {
    thead {
      tr th {
        padding: ${rem(27)} ${rem(14)} ${rem(27)} ${rem(14)};
      }
    }

    tbody tr {
      td:last-child {
        text-align: left;
      }

      td {
        padding-left: ${rem(8)};
        white-space: nowrap;
      }
    }
  }

  &.report-table--header {
    tr:nth-child(1) th {
      padding: 0;
      border-bottom: 0;
      cursor: auto;

      .sorting-icon {
        display: none !important;
      }
    }
  }

  /* ---- V2 sub-row / group alignment ---- */
  .indent-cell {
    padding: 0 !important;
    width: 0;
    min-width: 0;
    max-width: ${rem(32)};
  }

  .subrow-header-cell {
    font-weight: 500;
    font-size: ${rem(13)};
    color: var(--ui-table-header-color, #717171);
    white-space: nowrap;
    cursor: default;
  }

  tr.subrow-header th {
    cursor: default;
    background: transparent;
  }

  tr.group-row {
    td {
      border-bottom: ${rem(1)} solid var(--ui-table-border, var(--ui-border-muted));
    }
    &:hover {
      background-color: var(--ui-interactive-light, #f9f7ff);
    }
  }

  tr.child-row {
    td {
      border-bottom: ${rem(1)} solid var(--ui-table-border, var(--ui-border-muted));
      font-size: ${rem(13)};
    }
  }

  /* ---- V2 virtual scroll spacer reset ---- */
  tbody[data-virtual-spacer] {
    padding: 0;
    margin: 0;
    border: none;
  }

  tbody[data-virtual-spacer] tr {
    padding: 0;
    margin: 0;
    border: none;
    background: none !important;
  }

  tbody[data-virtual-spacer] tr td {
    padding: 0 !important;
    margin: 0;
    border: none !important;
    line-height: 0;
    font-size: 0;
    transition: none;
  }

  /* Measurement tbody wrappers */
  tbody[data-index] {
    padding: 0;
    margin: 0;
    border: none;
  }

  /* ---- V2 inline-edit cell containment ---- */
  tbody td[id^='cell.'] {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 0;
  }

  tbody td[id^='cell.'] > div {
    width: 100%;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  tbody td[id^='cell.'] > div > div {
    margin-bottom: 0 !important;
    min-width: 0;
  }

  tbody td[id^='cell.'] input,
  tbody td[id^='cell.'] textarea {
    min-width: 0 !important;
    max-width: 100% !important;
    width: 100% !important;
    box-sizing: border-box;
  }

  tbody td[id^='cell.'] .select-multiple,
  tbody td[id^='cell.'] [class*='container'] {
    min-width: 0 !important;
    width: 100%;
  }

  tbody td[id^='cell.'] .react-datepicker-wrapper,
  tbody td[id^='cell.'] .react-datepicker__input-container {
    width: 100%;
    display: block;
  }
`;

// ---------------------------------------------------------------------------
// Root — orchestrator + context provider
// ---------------------------------------------------------------------------

export interface DataTableRootProps extends React.HTMLAttributes<HTMLDivElement> {
  bordered?: boolean;
  border?: string;
  onSort?: (column: string, direction: 'asc' | 'desc' | '') => void;
  semanticId?: string;
  children: React.ReactNode;
}

const Root = React.forwardRef<HTMLDivElement, DataTableRootProps>(function Root(
  { bordered = false, border, onSort, semanticId, children, ...rest },
  ref
) {
  const semanticProps = getSemanticAttributes(semanticId);
  const [sortState, setSortState] = React.useState<SortState>({ column: null, direction: '' });

  const requestSort = React.useCallback(
    (accessor: string, sortable?: boolean) => {
      if (sortable === false || !onSort) return;
      setSortState((prev) => {
        const nextDir =
          prev.column === accessor && prev.direction === 'asc'
            ? 'desc'
            : prev.column === accessor && prev.direction === 'desc'
            ? ''
            : 'asc';
        onSort(accessor, nextDir);
        return { column: accessor, direction: nextDir };
      });
    },
    [onSort]
  );

  const ctx = React.useMemo<DataTableContextValue>(
    () => ({ bordered, sortState, onSortChange: onSort, requestSort }),
    [bordered, sortState, onSort, requestSort]
  );

  return (
    <DataTableContext.Provider value={ctx}>
      <RootWrapper ref={ref} $border={border} {...semanticProps} {...rest}>
        {children}
      </RootWrapper>
    </DataTableContext.Provider>
  );
});

// ---------------------------------------------------------------------------
// Content — scrollable container wrapping the <table>
// ---------------------------------------------------------------------------

export interface DataTableContentProps extends React.HTMLAttributes<HTMLDivElement> {
  height?: string | number;
  padding?: string | number;
  overflowY?: boolean;
  tableClassName?: string;
  tableStyle?: React.CSSProperties;
  tableId?: string;
  /** Rendered inside the scroll container but after </table> (load more, spinners). */
  footer?: React.ReactNode;
  children: React.ReactNode;
}

const Content = React.forwardRef<HTMLDivElement, DataTableContentProps>(function Content(
  { height, padding, overflowY, tableClassName, tableStyle, tableId, footer, children, ...rest },
  ref
) {
  const { bordered } = useDataTableContext();
  const h = height != null ? (typeof height === 'number' ? `${height}px` : height) : undefined;
  const p =
    padding != null ? (typeof padding === 'number' ? `${padding}px` : String(padding)) : undefined;

  return (
    <ScrollContainer ref={ref} $height={h} $padding={p} $overflowY={overflowY} {...rest}>
      <StyledTable id={tableId} style={tableStyle} $bordered={bordered} className={tableClassName}>
        {children}
      </StyledTable>
      {footer}
    </ScrollContainer>
  );
});

// ---------------------------------------------------------------------------
// Header — <thead> with sticky positioning + shadow
// ---------------------------------------------------------------------------

const StyledThead = styled.thead`
  /* thead styles inherited from StyledTable; this wrapper exists for DataTable.Header compound use */
`;

export interface DataTableHeaderProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  children: React.ReactNode;
}

const Header = React.forwardRef<HTMLTableSectionElement, DataTableHeaderProps>(function Header(
  { children, ...rest },
  ref
) {
  return (
    <StyledThead ref={ref} {...rest}>
      <tr>{children}</tr>
    </StyledThead>
  );
});

// ---------------------------------------------------------------------------
// HeaderCell — individual <th> with sorting affordance
// ---------------------------------------------------------------------------

const ThCell = styled.th.withConfig({
  shouldForwardProp: (prop) => !['$width', '$minWidth', '$sortable'].includes(String(prop)),
})<{ $width?: number; $minWidth?: number; $sortable?: boolean }>`
  ${({ $width }) => $width != null && `width: ${$width}px;`}
  ${({ $minWidth }) => $minWidth != null && `min-width: ${$minWidth}px;`}
  ${({ $sortable }) =>
    $sortable &&
    css`
      cursor: pointer;
      user-select: none;
    `}
`;

const ThInner = styled.div`
  display: flex;
  align-items: center;
  column-gap: ${rem(6)};

  span {
    font-weight: 500;
    color: var(--ui-pagination-muted, var(--ui-text-subtle));
  }

  .sorting-icon {
    display: flex;
    flex-direction: column;

    > span {
      font-size: ${rem(20)};
      width: ${rem(8)};
      height: ${rem(8)};
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--ui-text-subtle);
      transition: all 0.2s linear;

      &[data-active='true'] {
        color: var(--ui-text);
      }
    }
  }
`;

export interface DataTableHeaderCellProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  accessor?: string;
  sortable?: boolean;
  width?: number;
  minWidth?: number;
  align?: 'left' | 'center' | 'right';
  children: React.ReactNode;
}

function HeaderCell({
  accessor,
  sortable = false,
  width,
  minWidth,
  align = 'left',
  children,
  ...rest
}: DataTableHeaderCellProps) {
  const { sortState, onSortChange, requestSort } = useDataTableContext();
  const canSort = sortable && Boolean(onSortChange) && accessor != null;
  const isSortedAsc = canSort && sortState.column === accessor && sortState.direction === 'asc';
  const isSortedDesc = canSort && sortState.column === accessor && sortState.direction === 'desc';

  return (
    <ThCell
      $width={width}
      $minWidth={minWidth}
      $sortable={canSort}
      scope='col'
      onClick={canSort ? () => requestSort(accessor!, sortable) : undefined}
      aria-sort={isSortedAsc ? 'ascending' : isSortedDesc ? 'descending' : undefined}
      {...rest}>
      <ThInner style={{ justifyContent: align === 'center' ? 'center' : 'flex-start' }}>
        <span>{children}</span>
        {canSort && (
          <div className='sorting-icon'>
            <span className='material-symbols-rounded' data-active={isSortedAsc}>
              arrow_drop_up
            </span>
            <span className='material-symbols-rounded' data-active={isSortedDesc}>
              arrow_drop_down
            </span>
          </div>
        )}
      </ThInner>
    </ThCell>
  );
}

// ---------------------------------------------------------------------------
// Body — <tbody>
// ---------------------------------------------------------------------------

const StyledTbody = styled.tbody`
  /* tbody row styles inherited from StyledTable; this wrapper exists for DataTable.Body compound use */
`;

export interface DataTableBodyProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  children: React.ReactNode;
}

const Body = React.forwardRef<HTMLTableSectionElement, DataTableBodyProps>(function Body(
  { children, ...rest },
  ref
) {
  return (
    <StyledTbody ref={ref} {...rest}>
      {children}
    </StyledTbody>
  );
});

// ---------------------------------------------------------------------------
// Row — <tr> with optional click handler
// ---------------------------------------------------------------------------

const StyledTr = styled.tr.withConfig({
  shouldForwardProp: (prop) => !['$clickable', '$highlighted'].includes(String(prop)),
})<{ $clickable?: boolean; $highlighted?: boolean }>`
  ${({ $clickable }) =>
    $clickable &&
    css`
      cursor: pointer;
    `}
  ${({ $highlighted }) =>
    $highlighted &&
    css`
      background-color: var(--ui-interactive-light, var(--ui-background-muted));
    `}
`;

export interface DataTableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  clickable?: boolean;
  highlighted?: boolean;
  children: React.ReactNode;
}

const Row = React.forwardRef<HTMLTableRowElement, DataTableRowProps>(function Row(
  { clickable, highlighted, children, ...rest },
  ref
) {
  return (
    <StyledTr ref={ref} $clickable={clickable} $highlighted={highlighted} {...rest}>
      {children}
    </StyledTr>
  );
});

// ---------------------------------------------------------------------------
// Cell — <td>
// ---------------------------------------------------------------------------

const StyledTd = styled.td.withConfig({
  shouldForwardProp: (prop) => !['$align', '$width', '$minWidth'].includes(String(prop)),
})<{ $align?: string; $width?: number; $minWidth?: number }>`
  /* Base td styles inherited from StyledTable; only overrides for DataTable.Cell compound use */
  position: relative;
  ${({ $align }) => $align && `text-align: ${$align};`}
  ${({ $width }) => $width != null && `width: ${$width}px;`}
  ${({ $minWidth }) => $minWidth != null && `min-width: ${$minWidth}px;`}
`;

export interface DataTableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  align?: 'left' | 'center' | 'right';
  width?: number;
  minWidth?: number;
  children?: React.ReactNode;
}

const Cell = React.forwardRef<HTMLTableCellElement, DataTableCellProps>(function Cell(
  { align, width, minWidth, children, ...rest },
  ref
) {
  return (
    <StyledTd ref={ref} $align={align} $width={width} $minWidth={minWidth} {...rest}>
      {children}
    </StyledTd>
  );
});

// ---------------------------------------------------------------------------
// CheckboxCell — reusable checkbox for header or body rows
// ---------------------------------------------------------------------------

const CheckboxTd = styled.td`
  min-width: ${rem(10)};
  width: ${rem(10)};
  padding-left: ${rem(3)} !important;
`;

const CheckboxTh = styled.th`
  min-width: ${rem(10)};
  width: ${rem(10)};
`;

export interface DataTableCheckboxProps {
  checked: boolean;
  indeterminate?: boolean;
  onChange: (checked: boolean) => void;
  header?: boolean;
}

function Checkbox({ checked, indeterminate, onChange, header }: DataTableCheckboxProps) {
  const Wrapper = header ? CheckboxTh : CheckboxTd;
  const ref = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = Boolean(indeterminate);
    }
  }, [indeterminate]);

  return (
    <Wrapper>
      <DSCheckbox
        ref={ref}
        checked={checked}
        onChange={(e) => {
          onChange((e.target as HTMLInputElement).checked);
        }}
        onClick={(e) => e.stopPropagation()}
      />
    </Wrapper>
  );
}

// ---------------------------------------------------------------------------
// GroupRow — collapsible group header row for groupBy feature
// ---------------------------------------------------------------------------

const StyledGroupTr = styled.tr.withConfig({
  shouldForwardProp: (prop) => !['$expanded', '$depth'].includes(String(prop)),
})<{ $expanded?: boolean; $depth?: number }>`
  background-color: var(--ui-table-group-bg, var(--ui-background-muted));
  cursor: pointer;
  user-select: none;

  td {
    padding: ${rem(10)} ${rem(14)};
    font-weight: 700;
    font-size: ${rem(14)};
    color: var(--ui-table-group-color, var(--ui-text));
    border-bottom: ${rem(1)} solid var(--ui-table-border, var(--ui-border-muted));
    ${({ $depth }) => $depth != null && $depth > 0 && `padding-left: ${rem(14 + $depth * 20)};`}
  }
`;

const GroupToggleIcon = styled.span<{ $expanded?: boolean }>`
  display: inline-flex;
  align-items: center;
  margin-right: ${rem(8)};
  font-size: ${rem(20)};
  transition: transform 0.15s ease;
  transform: rotate(${({ $expanded }) => ($expanded ? '0deg' : '-90deg')});
  color: var(--ui-text-subtle);
`;

export interface DataTableGroupRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  expanded?: boolean;
  depth?: number;
  colSpan?: number;
  onToggle?: () => void;
  children: React.ReactNode;
}

function GroupRow({
  expanded = true,
  depth = 0,
  colSpan,
  onToggle,
  children,
  ...rest
}: DataTableGroupRowProps) {
  return (
    <StyledGroupTr $expanded={expanded} $depth={depth} onClick={onToggle} {...rest}>
      <td colSpan={colSpan}>
        <GroupToggleIcon $expanded={expanded} className='material-symbols-rounded'>
          expand_more
        </GroupToggleIcon>
        {children}
      </td>
    </StyledGroupTr>
  );
}

// ---------------------------------------------------------------------------
// SubRow — indented child row for sub-row expansion
// ---------------------------------------------------------------------------

const StyledSubTr = styled.tr.withConfig({
  shouldForwardProp: (prop) => !['$indent'].includes(String(prop)),
})<{ $indent?: boolean }>`
  background-color: var(--ui-table-subrow-bg, var(--ui-surface));

  td {
    border-bottom: ${rem(1)} solid var(--ui-table-border, var(--ui-border-muted));
    font-size: ${rem(13)};
    color: var(--ui-table-cell-color, var(--ui-text));
    ${({ $indent }) => $indent && `padding-left: ${rem(28)};`}
  }

  &:hover {
    background-color: var(--ui-table-hover, var(--ui-background-muted));
  }
`;

export interface DataTableSubRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  indent?: boolean;
  children: React.ReactNode;
}

const SubRow = React.forwardRef<HTMLTableRowElement, DataTableSubRowProps>(function SubRow(
  { indent = true, children, ...rest },
  ref
) {
  return (
    <StyledSubTr ref={ref} $indent={indent} {...rest}>
      {children}
    </StyledSubTr>
  );
});

// ---------------------------------------------------------------------------
// EditableCell — cell that renders an inline editor
// ---------------------------------------------------------------------------

const EditableCellWrapper = styled.td.withConfig({
  shouldForwardProp: (prop) => !['$hasError', '$align'].includes(String(prop)),
})<{ $hasError?: boolean; $align?: string }>`
  padding: ${rem(4)} ${rem(6)};
  position: relative;
  ${({ $align }) => $align && `text-align: ${$align};`}

  input, select, textarea {
    width: 100%;
    padding: ${rem(6)} ${rem(8)};
    font-size: ${rem(14)};
    border: ${rem(1)} solid
      ${({ $hasError }) =>
        $hasError ? 'var(--ui-error, #dc3545)' : 'var(--ui-input-border, var(--ui-border-muted))'};
    border-radius: var(--ui-radius-sm, ${rem(4)});
    background: var(--ui-surface);
    color: var(--ui-text);
    outline: none;
    transition: border-color 0.15s ease;

    &:focus {
      border-color: var(--ui-interactive);
      box-shadow: 0 0 0 ${rem(2)} var(--ui-interactive-light, rgba(0, 123, 255, 0.15));
    }
  }
`;

const EditableError = styled.div`
  font-size: ${rem(11)};
  color: var(--ui-error, #dc3545);
  margin-top: ${rem(2)};
  line-height: 1.3;
`;

export interface DataTableEditableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  error?: string;
  align?: 'left' | 'center' | 'right';
  children: React.ReactNode;
}

const EditableCell = React.forwardRef<HTMLTableCellElement, DataTableEditableCellProps>(
  function EditableCell({ error, align, children, ...rest }, ref) {
    return (
      <EditableCellWrapper ref={ref} $hasError={Boolean(error)} $align={align} {...rest}>
        {children}
        {error && <EditableError>{error}</EditableError>}
      </EditableCellWrapper>
    );
  }
);

// ---------------------------------------------------------------------------
// EmptyState — "No data" placeholder
// ---------------------------------------------------------------------------

const EmptyWrapper = styled.tr`
  td {
    padding: ${rem(48)} ${rem(16)};
    text-align: center;
    color: var(--ui-text-muted);
    font-size: ${rem(14)};
    border-bottom: none;
  }
`;

export interface DataTableEmptyStateProps {
  colSpan: number;
  message?: string;
  icon?: string;
  children?: React.ReactNode;
}

function EmptyState({
  colSpan,
  message = 'No data available',
  icon,
  children,
}: DataTableEmptyStateProps) {
  return (
    <EmptyWrapper>
      <td colSpan={colSpan}>
        {icon && (
          <span
            className='material-symbols-outlined'
            style={{ fontSize: rem(40), display: 'block', marginBottom: rem(8), opacity: 0.4 }}>
            {icon}
          </span>
        )}
        {children ?? message}
      </td>
    </EmptyWrapper>
  );
}

// ---------------------------------------------------------------------------
// LoadingState — skeleton rows
// ---------------------------------------------------------------------------

const SkeletonPulse = styled.div`
  height: ${rem(14)};
  border-radius: var(--ui-radius-sm, ${rem(4)});
  background: linear-gradient(
    90deg,
    var(--ui-skeleton-from, #eee) 25%,
    var(--ui-skeleton-to, #ddd) 50%,
    var(--ui-skeleton-from, #eee) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

export interface DataTableLoadingStateProps {
  colSpan: number;
  rows?: number;
}

function LoadingState({ colSpan, rows = 5 }: DataTableLoadingStateProps) {
  return (
    <>
      {Array.from({ length: rows }, (_, i) => (
        <tr key={`skeleton-${i}`}>
          {Array.from({ length: colSpan }, (_, j) => (
            <StyledTd key={`skeleton-${i}-${j}`}>
              <SkeletonPulse style={{ width: `${60 + Math.random() * 30}%` }} />
            </StyledTd>
          ))}
        </tr>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// ActionCell — cell with action dropdown trigger
// ---------------------------------------------------------------------------

const ActionTd = styled.td`
  text-align: center;
  width: ${rem(60)};
  min-width: ${rem(60)};
  position: relative;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${rem(4)};
  border-radius: var(--ui-radius-sm, ${rem(4)});
  color: var(--ui-text-subtle);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: var(--ui-background-muted);
    color: var(--ui-text);
  }

  .material-symbols-outlined {
    font-size: ${rem(20)};
  }
`;

export interface DataTableActionCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  onToggle?: () => void;
  open?: boolean;
  children?: React.ReactNode;
}

function ActionCell({ onToggle, open, children, ...rest }: DataTableActionCellProps) {
  return (
    <ActionTd {...rest}>
      <ActionButton onClick={onToggle} aria-expanded={open} aria-label='Row actions'>
        <span className='material-symbols-outlined'>more_vert</span>
      </ActionButton>
      {open && children}
    </ActionTd>
  );
}

// ---------------------------------------------------------------------------
// SelectAllBar — banner above table for bulk selection
// ---------------------------------------------------------------------------

const SelectBarWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${rem(10)} ${rem(16)};
  background: var(--ui-interactive-light, rgba(0, 123, 255, 0.08));
  border: ${rem(1)} solid var(--ui-interactive, #007bff);
  border-radius: var(--ui-radius-md, ${rem(6)});
  margin-bottom: ${rem(8)};
  font-size: ${rem(14)};
  color: var(--ui-text);
`;

const SelectBarActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
`;

export interface DataTableSelectAllBarProps {
  selectedCount: number;
  totalCount?: number;
  isAllPagesSelected?: boolean;
  onSelectAllPages?: () => void;
  onClearSelection?: () => void;
  children?: React.ReactNode;
}

function SelectAllBar({
  selectedCount,
  totalCount,
  isAllPagesSelected,
  onSelectAllPages,
  onClearSelection,
  children,
}: DataTableSelectAllBarProps) {
  return (
    <SelectBarWrapper>
      <span>
        <strong>{selectedCount}</strong> item{selectedCount !== 1 ? 's' : ''} selected
        {isAllPagesSelected && totalCount != null && ` (all ${totalCount})`}
      </span>
      <SelectBarActions>
        {!isAllPagesSelected && onSelectAllPages && (
          <button
            type='button'
            onClick={onSelectAllPages}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--ui-interactive)',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: rem(14),
            }}>
            Select all pages
          </button>
        )}
        {onClearSelection && (
          <button
            type='button'
            onClick={onClearSelection}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--ui-text-muted)',
              cursor: 'pointer',
              fontSize: rem(14),
            }}>
            Clear
          </button>
        )}
        {children}
      </SelectBarActions>
    </SelectBarWrapper>
  );
}

// ---------------------------------------------------------------------------
// Footer — slot for pagination or summary below the table
// ---------------------------------------------------------------------------

export interface DataTableFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const Footer = React.forwardRef<HTMLDivElement, DataTableFooterProps>(function Footer(
  { children, ...rest },
  ref
) {
  return (
    <div ref={ref} {...rest}>
      {children}
    </div>
  );
});

// ---------------------------------------------------------------------------
// ResizeHandle — column resize drag affordance
// ---------------------------------------------------------------------------

const StyledResizeHandle = styled.div`
  position: absolute;
  right: 2px;
  top: 0;
  bottom: 0;
  width: 7px;
  cursor: col-resize;
  user-select: none;
  touch-action: none;
  z-index: 1;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 3px;
    height: 30px;
    border-radius: 2px;
    background: transparent;
    transition: background 160ms ease, transform 160ms ease, box-shadow 160ms ease,
      height 200ms ease, border-radius 200ms ease;
  }

  th:hover > &::after {
    background: var(--ui-border, #cbd5e1);
  }

  &:hover::after {
    background: var(--ui-interactive, #384dd6);
    transform: translate(-50%, -50%) scaleY(1.1);
    box-shadow: 0 0 3px rgba(var(--ui-interactive-rgb, 56 77 214) / 0.3);
  }

  &[data-resizing='true']::after {
    background: var(--ui-interactive, #384dd6);
    height: 100%;
    border-radius: 0;
    box-shadow: 0 0 4px rgba(var(--ui-interactive-rgb, 56 77 214) / 0.35);
    transform: translate(-50%, -50%) scaleY(1);
  }
`;

export interface DataTableResizeHandleProps extends React.HTMLAttributes<HTMLDivElement> {
  resizing?: boolean;
}

function ResizeHandle({ resizing, ...rest }: DataTableResizeHandleProps) {
  return <StyledResizeHandle data-resizing={resizing || undefined} {...rest} />;
}

// ---------------------------------------------------------------------------
// SortableHeader — header label + sort direction icons
// ---------------------------------------------------------------------------

const SortableHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  column-gap: ${rem(6)};

  span {
    font-weight: 500;
    color: var(--ui-table-header-color, #717171);
  }

  > span:first-child {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-word;
  }

  .sorting-icon {
    display: flex;
    flex-direction: column;

    > span {
      font-size: ${rem(20)};
      width: ${rem(8)};
      height: ${rem(8)};
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--ui-text-subtle, #969696);
      transition: all 0.2s linear;

      &[data-active='true'],
      &.active {
        color: var(--ui-text, #161616);
      }
    }
  }
`;

export interface DataTableSortableHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** When provided, SortableHeader renders the label + sort icons automatically. */
  sortDirection?: 'asc' | 'desc' | '';
  children: React.ReactNode;
}

/**
 * Self-rendering variant: pass `sortDirection` to get label + sort arrows.
 * Wrapper variant: omit `sortDirection` and render your own children (icons etc).
 */
function SortableHeader({ sortDirection, children, ...rest }: DataTableSortableHeaderProps) {
  if (sortDirection != null) {
    return (
      <SortableHeaderWrapper {...rest}>
        <span>{children}</span>
        <div className='sorting-icon'>
          <span className='material-symbols-rounded' data-active={sortDirection === 'asc'}>
            arrow_drop_up
          </span>
          <span className='material-symbols-rounded' data-active={sortDirection === 'desc'}>
            arrow_drop_down
          </span>
        </div>
      </SortableHeaderWrapper>
    );
  }
  return <SortableHeaderWrapper {...rest}>{children}</SortableHeaderWrapper>;
}

// ---------------------------------------------------------------------------
// ActionTrigger — row action menu dot trigger (purple variant)
// ---------------------------------------------------------------------------

const StyledActionTrigger = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: ${rem(4)};
  border-radius: ${rem(4)};

  .material-symbols-outlined {
    font-size: ${rem(24)};
    color: var(--ui-interactive, #6b4d9a);
    opacity: 0.7;
    font-weight: 800;
    transition: opacity 0.2s linear;
  }

  &:hover .material-symbols-outlined,
  &[data-state='open'] .material-symbols-outlined {
    opacity: 1;
  }
`;

export interface DataTableActionTriggerProps extends React.HTMLAttributes<HTMLSpanElement> {
  open?: boolean;
  icon?: string;
}

const ActionTrigger = React.forwardRef<HTMLSpanElement, DataTableActionTriggerProps>(
  function ActionTrigger({ open, icon = 'more_vert', ...rest }, ref) {
    return (
      <StyledActionTrigger ref={ref} data-state={open ? 'open' : undefined} {...rest}>
        <span className='material-symbols-outlined'>{icon}</span>
      </StyledActionTrigger>
    );
  }
);

// ---------------------------------------------------------------------------
// Toolbar — search + filter + actions layout container
// ---------------------------------------------------------------------------

const ToolbarWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: ${rem(12)};
  align-items: start;
  padding: ${rem(0)} ${rem(12)} ${rem(12)};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ToolbarLeading = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${rem(12)};
`;

const ToolbarTrailing = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: ${rem(8)};
`;

export interface DataTableToolbarProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

function Toolbar({ children, ...rest }: DataTableToolbarProps) {
  return <ToolbarWrapper {...rest}>{children}</ToolbarWrapper>;
}

export interface DataTableToolbarSlotProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

function ToolbarLeadingSlot({ children, ...rest }: DataTableToolbarSlotProps) {
  return <ToolbarLeading {...rest}>{children}</ToolbarLeading>;
}

function ToolbarTrailingSlot({ children, ...rest }: DataTableToolbarSlotProps) {
  return <ToolbarTrailing {...rest}>{children}</ToolbarTrailing>;
}

// ---------------------------------------------------------------------------
// ResetFilterButton — reset filters action link
// ---------------------------------------------------------------------------

const StyledResetFilterButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s linear;
  font-family: var(--ui-font-sans);

  /* Mobile: bordered icon button matching Filter.Trigger */
  padding: ${rem(9)} ${rem(19)};
  border: ${rem(1)} solid var(--ui-border, #d9d9d9);
  border-radius: var(--ui-radius-md, ${rem(6)});
  background: var(--ui-surface, #fff);
  gap: ${rem(4)};
  margin-left: 0;
  margin-top: 0;

  & > [data-reset-label] {
    display: none;
  }

  &,
  & span {
    color: var(--ui-text-muted, #595959);
  }

  &:not(:disabled):hover {
    border-color: var(--ui-text-subtle, #999);
    box-shadow: 0 ${rem(4)} ${rem(6)} rgba(179, 179, 179, 0.24);
  }

  &:not(:disabled):hover,
  &:not(:disabled):hover span {
    color: var(--ui-interactive, #6b4d9a);
  }

  /* sm+: revert to text-link style */
  ${mediaQuery.sm} {
    padding: 0;
    border: none;
    border-radius: 0;
    background: none;
    box-shadow: none;
    justify-content: flex-start;
    gap: ${rem(2)};
    margin-left: ${rem(6)};
    margin-top: ${rem(8)};

    & > [data-reset-label] {
      display: inline;
    }

    &:not(:disabled):hover {
      border-color: transparent;
      box-shadow: none;
    }
  }
`;

export interface DataTableResetFilterButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
}

const ResetFilterButton = React.forwardRef<HTMLButtonElement, DataTableResetFilterButtonProps>(
  function ResetFilterButton({ children, ...rest }, ref) {
    return (
      <StyledResetFilterButton ref={ref} type='button' {...rest}>
        {children}
      </StyledResetFilterButton>
    );
  }
);

// ---------------------------------------------------------------------------
// BulkActionBar — toolbar bar for bulk selection actions
// ---------------------------------------------------------------------------

const BulkBarWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${rem(8)};
  margin: ${rem(18)} 0 ${rem(10)} 0;
`;

export interface DataTableBulkActionBarProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

function BulkActionBar({ children, ...rest }: DataTableBulkActionBarProps) {
  return <BulkBarWrapper {...rest}>{children}</BulkBarWrapper>;
}

// ---------------------------------------------------------------------------
// FilterChipBar — active filter chips displayed below toolbar
// ---------------------------------------------------------------------------

const FilterChipBarWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  overflow-x: auto;
  flex-wrap: nowrap;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  ${mediaQuery.sm} {
    flex-wrap: wrap;
    overflow-x: visible;
  }
`;

const FilterChipItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  border-radius: ${rem(4)};
  border: 1px solid var(--ui-border-muted, #d7d7d7);
  background: var(--ui-surface, #fff);
  padding: ${rem(6)} ${rem(12)} ${rem(4)} ${rem(12)};
  white-space: nowrap;
  flex-shrink: 0;
`;

export interface DataTableFilterChip {
  name: string;
  label: string;
  displayValue: string;
}

export interface DataTableFilterChipBarProps extends React.HTMLAttributes<HTMLDivElement> {
  chips: DataTableFilterChip[];
  onRemove: (name: string) => void;
  onClearAll?: () => void;
}

function FilterChipBar({ chips, onRemove, onClearAll: _onClearAll, ...rest }: DataTableFilterChipBarProps) {
  if (chips.length === 0) return null;
  return (
    <FilterChipBarWrapper {...rest}>
      {chips.map((chip) => (
        <FilterChipItem key={chip.name}>
          <span
            style={{
              fontSize: rem(14),
              lineHeight: rem(18),
              fontWeight: 600,
              color: 'var(--ui-text-muted, #717171)',
            }}>
            {chip.label}: {chip.displayValue}
          </span>
          <span
            className='material-symbols-outlined'
            role='button'
            tabIndex={0}
            aria-label={`Remove filter ${chip.label}`}
            onClick={() => onRemove(chip.name)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onRemove(chip.name);
              }
            }}
            style={{
              fontSize: rem(16),
              fontWeight: 'bold',
              color: '#717171',
              cursor: 'pointer',
            }}>
            close
          </span>
        </FilterChipItem>
      ))}
    </FilterChipBarWrapper>
  );
}

// ---------------------------------------------------------------------------
// ImportBar — wrapper for import/export actions (CSV template etc.)
// ---------------------------------------------------------------------------

const ImportBarOuter = styled.div`
  margin-bottom: ${rem(6)};

  ${mediaQuery.sm} {
    margin-bottom: ${rem(12)};
  }
`;

const ImportBarInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background-color: var(--ui-surface, #fff);
  border: ${rem(1)} solid var(--ui-border-muted, #f5f5f5);
  border-radius: ${rem(6)};
  padding: ${rem(10)} ${rem(12)};
  gap: ${rem(8)};
  overflow: hidden;

  .spinner-container {
    width: ${rem(15)};
    height: ${rem(14)};
  }

  ${mediaQuery.sm} {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    padding: ${rem(12)} ${rem(16)};
    gap: ${rem(10)};
  }

  ${mediaQuery.md} {
    padding: ${rem(15)} ${rem(20)};
    gap: ${rem(12)};
  }
`;

export interface DataTableImportBarProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

function ImportBar({ children, ...rest }: DataTableImportBarProps) {
  return (
    <ImportBarOuter {...rest}>
      <ImportBarInner>{children}</ImportBarInner>
    </ImportBarOuter>
  );
}

// ---------------------------------------------------------------------------
// FileInputLayout — row of file inputs / action buttons
// ---------------------------------------------------------------------------

const StyledFileInputLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${rem(9)};
  width: 100%;

  /* On mobile grid, stretch child buttons/labels to fill cells */
  & > * {
    min-width: 0;
  }

  button,
  .input-file-container,
  .import-label {
    width: 100%;
    text-align: center;
    box-sizing: border-box;
  }

  .spinner-container {
    width: 18px;
    height: 18px;
  }

  ${mediaQuery.sm} {
    display: flex;
    flex-direction: row;
    width: auto;

    button,
    .input-file-container,
    .import-label {
      width: auto;
    }
  }

  .input-file-container {
    position: relative;
    font-weight: 500;
    font-size: ${rem(14)};
    line-height: ${rem(18)};
    border: ${rem(1)} solid var(--ui-interactive);
    border-radius: ${rem(6)};
    padding: ${rem(6)} ${rem(8)};
    transition: all 300ms ease;
    color: var(--ui-interactive);

    &:not(:disabled):hover {
      box-shadow: ${rem(0)} ${rem(4)} ${rem(6)} rgba(var(--ui-interactive-rgb, 100 82 217) / 0.24);
    }

    &:disabled {
      border-color: rgba(var(--ui-interactive-rgb, 100 82 217) / 0.2);
      color: rgba(var(--ui-interactive-rgb, 100 82 217) / 0.5);
    }
  }

  .input-file-dropdown {
    position: relative;
    font-weight: 500;
    font-size: ${rem(14)};
    line-height: ${rem(18)};
    transition: all 300ms ease;
    cursor: pointer;
  }

  .input-file-dropdown-trigger {
    display: block;
    font-weight: 500;
    font-size: 14px;
    white-space: nowrap;
    width: 220px;
    color: var(--ui-text-muted);
    border-radius: 4px;
    padding: 10px 14px;
    transition: all 0.2s linear;
    cursor: pointer;

    &:hover {
      background: var(--ui-interactive-light);
      color: var(--ui-interactive);
      font-weight: 500;
    }
  }

  .input-file-trigger {
    display: block;
    font-weight: 500;
  }

  .input-file {
    position: absolute;
    top: 0;
    left: 0;
    color: #fff;
    width: 100%;
    height: 100%;
    opacity: 0;
    visibility: hidden;
  }
`;

export interface DataTableFileInputLayoutProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

function FileInputLayout({ children, ...rest }: DataTableFileInputLayoutProps) {
  return <StyledFileInputLayout {...rest}>{children}</StyledFileInputLayout>;
}

// ---------------------------------------------------------------------------
// displayName
// ---------------------------------------------------------------------------

Header.displayName = 'DataTable.Header';
HeaderCell.displayName = 'DataTable.HeaderCell';
Body.displayName = 'DataTable.Body';
Row.displayName = 'DataTable.Row';
Cell.displayName = 'DataTable.Cell';
Checkbox.displayName = 'DataTable.Checkbox';
GroupRow.displayName = 'DataTable.GroupRow';
SubRow.displayName = 'DataTable.SubRow';
EditableCell.displayName = 'DataTable.EditableCell';
EmptyState.displayName = 'DataTable.EmptyState';
LoadingState.displayName = 'DataTable.LoadingState';
ActionCell.displayName = 'DataTable.ActionCell';
SelectAllBar.displayName = 'DataTable.SelectAllBar';
Footer.displayName = 'DataTable.Footer';
ResizeHandle.displayName = 'DataTable.ResizeHandle';
SortableHeader.displayName = 'DataTable.SortableHeader';
ActionTrigger.displayName = 'DataTable.ActionTrigger';
Toolbar.displayName = 'DataTable.Toolbar';
ToolbarLeadingSlot.displayName = 'DataTable.ToolbarLeading';
ToolbarTrailingSlot.displayName = 'DataTable.ToolbarTrailing';
ResetFilterButton.displayName = 'DataTable.ResetFilterButton';
BulkActionBar.displayName = 'DataTable.BulkActionBar';
FilterChipBar.displayName = 'DataTable.FilterChipBar';
ImportBar.displayName = 'DataTable.ImportBar';
FileInputLayout.displayName = 'DataTable.FileInputLayout';

// ---------------------------------------------------------------------------
// Compound export
// ---------------------------------------------------------------------------

export const DataTable = {
  Root,
  Content,
  Header,
  HeaderCell,
  Body,
  Row,
  Cell,
  Checkbox,
  GroupRow,
  SubRow,
  EditableCell,
  EmptyState,
  LoadingState,
  ActionCell,
  SelectAllBar,
  Footer,
  ResizeHandle,
  SortableHeader,
  ActionTrigger,
  Toolbar,
  ToolbarLeading: ToolbarLeadingSlot,
  ToolbarTrailing: ToolbarTrailingSlot,
  ResetFilterButton,
  BulkActionBar,
  FilterChipBar,
  ImportBar,
  FileInputLayout,
};
